import React, { useState } from "react";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { BACKEND_BASE_URL } from "../config/api.config";

/**
 * Admin component to charge customers for no-shows
 * This can be integrated into your admin dashboard or therapist portal
 */
export default function AdminChargeNoShow({ booking, onSuccess }) {
  const [charging, setCharging] = useState(false);
  const [error, setError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [amount, setAmount] = useState(booking?.sessionPrice || 120);
  const [reason, setReason] = useState("no-show");

  const hasPaymentMethod = booking?.payment?.stripePaymentMethodId;
  const alreadyCharged = booking?.payment?.charged;

  const handleCharge = async () => {
    setError("");
    setCharging(true);

    try {
      // Get auth token from localStorage or wherever you store it
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || cookieStore.get('token')?.value;

      const headers = {
        "Content-Type": "application/json",
      };

      // Add auth token if available
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${BACKEND_BASE_URL}/api/payment/charge-no-show`, {
        method: "POST",
        headers: headers,
        credentials: "include", // This sends cookies too
        body: JSON.stringify({
          bookingId: booking._id,
          amount: amount,
          reason: reason,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to charge customer");
      }

      const data = await response.json();
      setShowConfirm(false);
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (err) {
      console.error("Charge error:", err);
      setError(err.message || "Failed to charge customer");
    } finally {
      setCharging(false);
    }
  };

  if (!hasPaymentMethod) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm text-gray-600">
          No payment method on file for this booking
        </p>
      </div>
    );
  }

  if (alreadyCharged) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
        <div className="flex items-center gap-2">
          <span className="text-green-600">✓</span>
          <div className="flex-1">
            <p className="text-sm font-medium text-green-900">
              Already charged ${booking.payment.amount}
            </p>
            <p className="text-xs text-green-700">
              Charge ID: {booking.payment.chargeId}
            </p>
            {booking.payment.chargedAt && (
              <p className="text-xs text-green-700">
                Charged on: {new Date(booking.payment.chargedAt).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!showConfirm) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <h4 className="text-sm font-medium text-amber-900 mb-1">
              No-Show Charge Available
            </h4>
            <p className="text-xs text-amber-700 mb-2">
              Payment method on file. You can charge this customer for a no-show or late cancellation.
            </p>
            <p className="text-xs text-amber-600">
              Customer: {booking.clientEmail}
            </p>
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowConfirm(true)}
          >
            Charge No-Show
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-red-900 mb-1">
          Confirm No-Show Charge
        </h4>
        <p className="text-xs text-red-700 mb-3">
          This will charge the customer's card on file. This action cannot be undone.
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium text-gray-700 mb-1 block">
            Amount (CAD)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(parseFloat(e.target.value))}
            className="w-full h-9 rounded-lg border border-gray-300 px-3 text-sm"
            placeholder="120.00"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-gray-700 mb-1 block">
            Reason
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full h-9 rounded-lg border border-gray-300 px-3 text-sm"
          >
            <option value="no-show">No-Show</option>
            <option value="late-cancellation">Late Cancellation (less than 2 hours)</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="rounded-lg bg-white border border-gray-200 p-3">
          <p className="text-xs text-gray-600 mb-1">
            <strong>Customer:</strong> {booking.clientName} ({booking.clientEmail})
          </p>
          <p className="text-xs text-gray-600 mb-1">
            <strong>Appointment:</strong> {new Date(booking.appointmentDate).toLocaleString()}
          </p>
          <p className="text-xs text-gray-600">
            <strong>Amount to charge:</strong> ${amount.toFixed(2)} CAD
          </p>
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-600 bg-red-100 rounded p-2">
          {error}
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setShowConfirm(false);
            setError("");
          }}
          disabled={charging}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={handleCharge}
          disabled={charging || !amount}
          className="flex-1"
        >
          {charging ? "Processing..." : `Charge $${amount.toFixed(2)}`}
        </Button>
      </div>
    </div>
  );
}
